import { Component, OnInit, OnDestroy, EventEmitter, Output, ViewChild, ElementRef, Input } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";

import { MenuItem } from "./menu.model";
import { environment } from "src/environments/environment";
import { AuthenticationService } from "src/app/core/services/auth.service";
import { PermissionEnum } from "src/app/core/enum/permission";
import { NotificationService, NotificationPayload } from "src/app/core/services/notification.service";
import { Subscription } from "rxjs";

@Component({
	selector: "app-sidebar",
	templateUrl: "./sidebar.component.html",
	styleUrl: "./sidebar.component.scss",
})
export class SidebarComponent implements OnInit, OnDestroy {
	menu: any;
	toggle: any = true;
	menuItems: MenuItem[] = [];
	@Input() isCondensed: boolean;
	@ViewChild("sideMenu") sideMenu!: ElementRef;
	@Output() mobileMenuButtonClicked = new EventEmitter();
	MENU: MenuItem[] = [];
	private notifSub?: Subscription;
	constructor(
		private router: Router,
		public translate: TranslateService,
		private authenticationService: AuthenticationService,
		private notificationService: NotificationService,
	) {
		translate.setDefaultLang("fr");
	}

	ngOnInit(): void {
		// Menu Items
		this.menuItems = this.getMenu();
		this.router.events.subscribe((event) => {
			if (document.documentElement.getAttribute("data-layout") != "twocolumn") {
				if (event instanceof NavigationEnd) {
					this.initActiveMenu();
				}
			}
		});

		this.notificationService.startAutoRefresh();
		this.notifSub = this.notificationService.counts$.subscribe((p) => this.applyBadges(p));
	}

	ngOnDestroy(): void {
		this.notifSub?.unsubscribe();
	}

	private applyBadges(payload: NotificationPayload): void {
		if (!payload || !this.menuItems?.length) return;
		const setBadge = (item: any, count: number) => {
			if (!item) return;
			if (count > 0) {
				item.badge = { variant: "bg-primary-subtle text-primary rounded-pill fs-10", text: String(count) };
			} else {
				item.badge = undefined;
			}
		};

		const demandeMod = payload.modules.find((m) => m.module === "demande");
		const financeMod = payload.modules.find((m) => m.module === "finance");
		const rhMod = payload.modules.find((m) => m.module === "rh");

		const findById = (id: number, items: any[]): any => {
			for (const it of items) {
				if (it.id === id) return it;
				if (it.subItems) {
					const f = findById(id, it.subItems);
					if (f) return f;
				}
			}
			return null;
		};
		const findByLink = (link: string, items: any[]): any => {
			for (const it of items) {
				if (it.link === link) return it;
				if (it.subItems) {
					const f = findByLink(link, it.subItems);
					if (f) return f;
				}
			}
			return null;
		};

		const demandesMenu = findById(6, this.menuItems);
		const financeMenu = findById(56, this.menuItems);
		const rhMenu = findById(43, this.menuItems);
		setBadge(demandesMenu, demandeMod?.total ?? 0);
		setBadge(financeMenu, financeMod?.total ?? 0);
		setBadge(rhMenu, rhMod?.total ?? 0);

		const demandeAchatItem = findById(7, this.menuItems);
		const demandePrestationItem = findById(81, this.menuItems);
		const demandePaiementItem = findById(57, this.menuItems);
		const rhDemandesItem = findByLink("/rh/demandes", this.menuItems);
		const rhNoteFraisItem = findByLink("/rh/note-frais", this.menuItems);

		const findKey = (key: string) => {
			for (const m of payload.modules) {
				const it = m.items.find((i) => i.key === key);
				if (it) return it.count;
			}
			return 0;
		};
		setBadge(demandeAchatItem, findKey("demande_achat"));
		setBadge(demandePrestationItem, findKey("demande_prestation"));
		setBadge(demandePaiementItem, findKey("demande_paiement"));

		const demandeLocationItem = findById(82, this.menuItems);
		setBadge(demandeLocationItem, findKey("demande_location"));

		const rhDemandesTotal =
			findKey("demande_conge") +
			findKey("demande_attestation_salaire") +
			findKey("demande_attestation_travail") +
			findKey("demande_avertissement") +
			findKey("demande_bulletin_paie") +
			findKey("demande_domiciliation_salaire") +
			findKey("demande_reconnaissance_dette") +
			findKey("demande_stc") +
			findKey("rh_conge") +
			findKey("rh_domiciliation") +
			findKey("rh_avertissement") +
			findKey("rh_stc") +
			findKey("rh_attestation_salaire") +
			findKey("rh_attestation_travail") +
			findKey("rh_bulletin_paie");
		setBadge(rhDemandesItem, rhDemandesTotal);

		// Per-type counts for the RH "Demandes" sub-menu (Strapi v4 status counts)
		const rhDemandeCongeItem = findById(451, this.menuItems);
		const rhDemandeAttSalaireItem = findById(452, this.menuItems);
		const rhDemandeAttTravailItem = findById(453, this.menuItems);
		const rhDemandeBulletinItem = findById(454, this.menuItems);
		setBadge(rhDemandeCongeItem, findKey("demande_conge") + findKey("rh_conge"));
		setBadge(rhDemandeAttSalaireItem, findKey("demande_attestation_salaire") + findKey("rh_attestation_salaire"));
		setBadge(rhDemandeAttTravailItem, findKey("demande_attestation_travail") + findKey("rh_attestation_travail"));
		setBadge(rhDemandeBulletinItem, findKey("demande_bulletin_paie") + findKey("rh_bulletin_paie"));

		setBadge(rhNoteFraisItem, findKey("note_frais"));
	}

	get isAddDmd() {
		return this.authenticationService.isDemandeur() || this.authenticationService.isAdmin() || this.authenticationService.isChefChantier();
	}
	/***
	 * Activate droup down set
	 */
	ngAfterViewInit() {
		setTimeout(() => {
			this.initActiveMenu();
		}, 0);
	}

	removeActivation(items: any) {
		items.forEach((item: any) => {
			item.classList.remove("active");
		});
	}

	toggleItem(item: any) {
		this.menuItems.forEach((menuItem: any) => {
			if (menuItem == item) {
				menuItem.isCollapsed = !menuItem.isCollapsed;
			} else {
				menuItem.isCollapsed = true;
			}
			if (menuItem.subItems) {
				menuItem.subItems.forEach((subItem: any) => {
					if (subItem == item) {
						menuItem.isCollapsed = !menuItem.isCollapsed;
						subItem.isCollapsed = !subItem.isCollapsed;
					} else {
						subItem.isCollapsed = true;
					}
					if (subItem.subItems) {
						subItem.subItems.forEach((childitem: any) => {
							if (childitem == item) {
								childitem.isCollapsed = !childitem.isCollapsed;
								subItem.isCollapsed = !subItem.isCollapsed;
								menuItem.isCollapsed = !menuItem.isCollapsed;
							} else {
								childitem.isCollapsed = true;
							}
							if (childitem.subItems) {
								childitem.subItems.forEach((childrenitem: any) => {
									if (childrenitem == item) {
										childrenitem.isCollapsed = false;
										childitem.isCollapsed = false;
										subItem.isCollapsed = false;
										menuItem.isCollapsed = false;
									} else {
										childrenitem.isCollapsed = true;
									}
								});
							}
						});
					}
				});
			}
		});
	}

	// remove active items of two-column-menu
	activateParentDropdown(item: any) {
		item.classList.add("active");
		let parentCollapseDiv = item.closest(".collapse.menu-dropdown");

		if (parentCollapseDiv) {
			// to set aria expand true remaining
			parentCollapseDiv.parentElement.children[0].classList.add("active");

			if (parentCollapseDiv.parentElement.closest(".collapse.menu-dropdown")) {
				parentCollapseDiv.parentElement.closest(".collapse").classList.add("show");
				if (parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling) parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.classList.add("active");
				if (parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse")) {
					parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse").classList.add("show");
					parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse").previousElementSibling.classList.add("active");
				}
			}
			return false;
		}
		return false;
	}

	// Groupe du dernier lien cliqué : certains liens existent dans deux groupes (raccourcis
	// du Dashboard), il faut rouvrir celui d'où vient le clic et non le premier trouvé.
	private lastClickedParentId: string | null = null;

	updateActive(event: any) {
		this.lastClickedParentId = event.target?.closest?.("a")?.getAttribute("data-parent") ?? null;
		const ul = document.getElementById("navbar-nav");
		if (ul) {
			const items = Array.from(ul.querySelectorAll("a.nav-link"));
			this.removeActivation(items);
		}
		this.activateParentDropdown(event.target);
		this.closeSidebarOnMobile();
	}

	initActiveMenu() {
		let pathName = window.location.pathname;
		// Check if the application is running in production
		if (environment.production) {
			// Modify pathName for production build
			pathName = pathName.replace("/velzon/angular/material", "");
		}

		const parentId = this.lastClickedParentId;
		const active = this.pickMenuMatch(this.findMenuItems(pathName, this.menuItems), (item) => String(item.parentId) === parentId);
		this.toggleItem(active);
		const ul = document.getElementById("navbar-nav");
		if (ul) {
			const items = Array.from(ul.querySelectorAll("a.nav-link"));
			let activeItems = items.filter((x: any) => x.classList.contains("active"));
			this.removeActivation(activeItems);

			const matchingMenuItems = items.filter((x: any) => {
				if (environment.production) {
					let path = x.pathname;
					path = path.replace("/velzon/angular/material", "");
					return path === pathName;
				} else {
					return x.pathname === pathName;
				}
			});
			const matchingMenuItem = this.pickMenuMatch(matchingMenuItems, (x: any) => x.getAttribute("data-parent") === parentId);
			if (matchingMenuItem) {
				this.activateParentDropdown(matchingMenuItem);
			}
		}
	}

	/** Toutes les entrées visibles pointant vers ce chemin (un lien peut exister dans deux groupes). */
	private findMenuItems(pathname: string, menuItems: any[]): any[] {
		const found: any[] = [];
		for (const menuItem of menuItems) {
			// Une entrée masquée pour l'utilisateur ne doit pas décider du groupe à ouvrir
			if (menuItem.isActive === false) continue;
			if (menuItem.link && menuItem.link === pathname) found.push(menuItem);
			if (menuItem.subItems) found.push(...this.findMenuItems(pathname, menuItem.subItems));
		}
		return found;
	}

	/**
	 * Parmi des entrées en double : celle du groupe cliqué, sinon la dernière
	 * (les raccourcis du Dashboard sont en tête de menu, le module d'origine vient après).
	 */
	private pickMenuMatch<T>(matches: T[], isFromClickedGroup: (match: T) => boolean): T | null {
		if (!matches.length) return null;
		return (this.lastClickedParentId != null && matches.find(isFromClickedGroup)) || matches[matches.length - 1];
	}

	/**
	 * Returns true or false if given menu item has child or not
	 * @param item menuItem
	 */
	hasItems(item: MenuItem) {
		return item.subItems !== undefined ? item.subItems.length > 0 : false;
	}

	/**
	 * Toggle the menu bar when having mobile screen
	 */
	toggleMobileMenu(event: any) {
		var sidebarsize = document.documentElement.getAttribute("data-sidebar-size");
		if (sidebarsize == "sm-hover-active") {
			document.documentElement.setAttribute("data-sidebar-size", "sm-hover");
		} else {
			document.documentElement.setAttribute("data-sidebar-size", "sm-hover-active");
		}
	}

	/**
	 * SidebarHide modal
	 * @param content modal content
	 */
	SidebarHide() {
		document.body.classList.remove("vertical-sidebar-enable");
	}

	/** Mobile : refermer le menu dès qu'un lien est cliqué. */
	closeSidebarOnMobile() {
		if (document.documentElement.clientWidth <= 767) {
			this.SidebarHide();
		}
	}
	getMenu() {
		return [
			// ADMIN ==========================================
			{
				id: 1,
				label: "MENUITEMS.ADMIN.TEXT",
				isTitle: true,
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.DIRECTION]),
			},
			{
				id: 2,
				label: "MENUITEMS.ADMIN.LIST.DASHBOARD",
				icon: "ri-bar-chart-line",
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.DIRECTION, PermissionEnum.FINANCE, PermissionEnum.MAGASINIER, PermissionEnum.ACHETEUR, PermissionEnum.SUPERVISEUR_ACHAT, PermissionEnum.MASTER_DATA]),
				subItems: [
					{
						id: 3,
						label: "MENUITEMS.MODULES.LIST.DASHBOARD_PROJETC",
						parentId: 2,
						link: "/dashboard/project",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.DIRECTION, PermissionEnum.FINANCE]),
					},
					{
						id: 4,
						label: "MENUITEMS.MODULES.LIST.DASHBOARD_FINANCIER",
						parentId: 2,
						link: "/dashboard/general",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.DIRECTION, PermissionEnum.FINANCE]),
					},
					{
						id: 103,
						label: "Flux article",
						parentId: 2,
						link: "/administration/flux-article",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.MAGASINIER, PermissionEnum.ACHETEUR, PermissionEnum.SUPERVISEUR_ACHAT, PermissionEnum.MASTER_DATA]),
					},
					{
						id: 5,
						label: "MENUITEMS.MODULES.LIST.DASHBOARD_LIFECYCLE",
						parentId: 2,
						link: "/dashboard/lifecycle",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.DIRECTION]),
					},
					{
						// Raccourci : le même lien existe aussi sous Logistique (id 16)
						id: 1601,
						label: "MENUITEMS.LOGISTIQUE.LIST.CHARGES",
						icon: " ri-article-line",
						parentId: 2,
						link: "/logistique/charges",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.DIRECTION]),
					},
					{
						id: 30,
						parentId: 2,
						label: "MENUITEMS.GESTION_COMMERCIAL.LIST.FACTURE",
						link: "/gestion-commercial/factures",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.DIRECTION, PermissionEnum.FINANCE]),
					},
					{
						id: 34,
						parentId: 2,
						label: "MENUITEMS.GESTION_COMMERCIAL.LIST.SUIVIE_RECOUVREMENT",
						link: "/gestion-commercial/suive-recouvrement",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.DIRECTION, PermissionEnum.FINANCE]),
					},
					{
						id: 24,
						label: "MENUITEMS.MODULES.LIST.RECEPTIONCHANTIER",
						parentId: 2,
						link: "/achat/rest-a-livre",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.DIRECTION, PermissionEnum.FINANCE]),
					},
				],
			},

			// MODULES ============================================
			{
				id: 5,
				label: "MENUITEMS.MODULES.TEXT",
				isTitle: true,
				isActive: this.authenticationService.hasPermission([
					PermissionEnum.ADMIN,
					PermissionEnum.CONTROLE_GESTION,
					PermissionEnum.TRESORERIE,
					PermissionEnum.SUPERVISEUR,
					PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA,
					PermissionEnum.ACHETEUR,
					PermissionEnum.SUPERVISEUR_ACHAT,
					PermissionEnum.BET,
					PermissionEnum.GESTIONNAIRE_COMMERCIAL,
					PermissionEnum.SUPERVISEUR_BET,
					PermissionEnum.CHIFFRAGE,
					PermissionEnum.COLLABORATEUR,
					PermissionEnum.TRESORERIE_BASIC,
					PermissionEnum.DEMANDEUR,
					PermissionEnum.RH,
					PermissionEnum.MAGASINIER_INFORMATIQUE,
					PermissionEnum.COORDINATION_RH,
					PermissionEnum.RESPONSABLE_PROJET,
				]),
			},
			{
				id: 6,
				label: "MENUITEMS.MODULES.LIST.DEMANDES",
				icon: "mdi mdi-format-list-checks",
				isActive: this.authenticationService.hasPermission([
					PermissionEnum.ADMIN,
					PermissionEnum.RH,
					PermissionEnum.ACHETEUR,
					PermissionEnum.SUPERVISEUR_ACHAT,
					PermissionEnum.SUPERVISEUR_BET,
					PermissionEnum.GESTIONNAIREBC,
					PermissionEnum.GESTIONNAIRE_COMMERCIAL,
					PermissionEnum.BET,
					PermissionEnum.SUPERVISEUR,
					PermissionEnum.DEMANDEUR,
					PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA,
					PermissionEnum.COLLABORATEUR,
					PermissionEnum.TRESORERIE_BASIC,
					PermissionEnum.TRESORERIE,
					PermissionEnum.CONTROLE_GESTION,
					PermissionEnum.MAGASINIER_INFORMATIQUE,
					PermissionEnum.COORDINATION_RH,
				]),
				subItems: [
					{
						id: 7,
						label: "MENUITEMS.MODULES.LIST.DEMANDESLISTE",
						parentId: 6,
						link: "/demandes/liste",
						isActive: this.authenticationService.hasPermission([
							PermissionEnum.DEMANDEUR,
							PermissionEnum.ADMIN,
							PermissionEnum.CONTROLE_GESTION,
							PermissionEnum.CHEF_CHANTIER,
							PermissionEnum.SUPERVISEUR, PermissionEnum.TRESORERIE,
						]),
					},
					{
						id: 81,
						label: "MENUITEMS.MODULES.LIST.DEMANDE_PRESTATION",
						parentId: 6,
						link: "/demandes/prestations",
						isActive: this.authenticationService.hasPermission([
							PermissionEnum.ADMIN,
							PermissionEnum.RH,
							PermissionEnum.ACHETEUR,
							PermissionEnum.SUPERVISEUR_ACHAT,
							PermissionEnum.SUPERVISEUR_BET,
							PermissionEnum.GESTIONNAIREBC,
							PermissionEnum.GESTIONNAIRE_COMMERCIAL,
							PermissionEnum.BET,
							PermissionEnum.SUPERVISEUR,
							PermissionEnum.DEMANDEUR,
							PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA,
							PermissionEnum.COLLABORATEUR,
							PermissionEnum.TRESORERIE_BASIC,
							PermissionEnum.TRESORERIE,
							PermissionEnum.CONTROLE_GESTION,
							PermissionEnum.MAGASINIER_INFORMATIQUE,
							PermissionEnum.COORDINATION_RH,
						]),
					},
					{
						id: 82,
						label: "Demande de loyer",
						parentId: 6,
						link: "/demandes/locations",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.SUPERVISEUR, PermissionEnum.COORDINATION_RH, PermissionEnum.TRESORERIE]),
					},
					{
						id: 8,
						label: "MENUITEMS.MODULES.LIST.RECEPTIONCHANTIER",
						parentId: 6,
						link: "/demandes/rest-a-livre",
						isActive: this.authenticationService.hasPermission([
							PermissionEnum.DEMANDEUR,
							PermissionEnum.ADMIN,
							PermissionEnum.CONTROLE_GESTION,
							PermissionEnum.SUPERVISEUR,
							PermissionEnum.CHEF_CHANTIER, PermissionEnum.TRESORERIE,
						]),
					},
				],
			},
			{
				id: 9,
				label: "MENUITEMS.MODULES.LIST.MAGASIN",
				icon: "mdi mdi-store-outline",
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.MAGASINIER_INFORMATIQUE, PermissionEnum.TRESORERIE]),
				subItems: [
					{
						id: 10,
						label: "MENUITEMS.MODULES.LIST.DEMANDES",
						parentId: 9,
						link: "/magasin/demandes",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.TRESORERIE]),
					},
					{
						id: 10,
						label: "MENUITEMS.MODULES.LIST.PREPARATION",
						parentId: 9,
						link: "/magasin/liste",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.MAGASINIER_INFORMATIQUE, PermissionEnum.TRESORERIE]),
					},
					{
						id: 11,
						label: "MENUITEMS.MODULES.LIST.BONCOMMANDE",
						icon: " ri-article-line",
						parentId: 9,
						link: "/magasin/bons-commandes",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.TRESORERIE]),
					},
					{
						id: 12,
						label: "MENUITEMS.MODULES.LIST.RECEPTIONS",
						parentId: 9,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.MAGASINIER_INFORMATIQUE, PermissionEnum.TRESORERIE]),
						subItems: [
							{
								id: 1201,
								parentId: 12,
								label: "MENUITEMS.MODULES.LIST.ARECEPTIONNER",
								link: "/magasin/receptions/a-receptionner",
								isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.MAGASINIER_INFORMATIQUE, PermissionEnum.TRESORERIE]),
							},
							{
								id: 1202,
								parentId: 12,
								label: "MENUITEMS.MODULES.LIST.RECEPTION",
								link: "/magasin/bons-livraison",
								isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.MAGASINIER_INFORMATIQUE, PermissionEnum.TRESORERIE]),
							},
							{
								id: 1204,
								parentId: 12,
								label: "Retours fournisseurs",
								link: "/magasin/retours-fournisseurs",
								isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.MAGASINIER, PermissionEnum.MAGASINIER_INFORMATIQUE, PermissionEnum.MASTER_DATA]),
							},
						],
					},
					{
						id: 1205,
						label: "Transferts",
						parentId: 9,
						link: "/magasin/transferts",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.MAGASINIER, PermissionEnum.MAGASINIER_INFORMATIQUE]),
					},
					{
						id: 1203,
						label: "MENUITEMS.MODULES.LIST.DEPOTS",
						parentId: 9,
						link: "/magasin/depots",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN]),
					},
					{
						id: 13,
						label: "MENUITEMS.MODULES.LIST.BONSSORTIES",
						link: "/bons-sorties/list",
						parentId: 9,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.TRESORERIE]),
					},
					{
						id: 14,
						label: "MENUITEMS.MODULES.LIST.RECEPTIONCHANTIER",
						parentId: 9,
						link: "/magasin/rest-a-livre",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.TRESORERIE]),
					},
				],
			},
			// STOCK ==============================================
			{
				id: 100,
				label: "Stock",
				icon: "ri-stack-line",
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.MAGASINIER, PermissionEnum.ACHETEUR, PermissionEnum.SUPERVISEUR_ACHAT, PermissionEnum.MASTER_DATA]),
				subItems: [
					{
						id: 101,
						label: "Stock initial",
						parentId: 100,
						link: "/administration/stock-initial",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.MAGASINIER, PermissionEnum.ACHETEUR, PermissionEnum.SUPERVISEUR_ACHAT, PermissionEnum.MASTER_DATA]),
					},
					{
						id: 102,
						label: "Dépôts",
						parentId: 100,
						link: "/magasin/depots",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN]),
					},
				],
			},
			{
				id: 15,
				label: "MENUITEMS.LOGISTIQUE.TEXT",
				icon: " ri-truck-line",
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.TRESORERIE]),
				subItems: [
					{
						id: 16,
						label: "MENUITEMS.LOGISTIQUE.LIST.CHARGES",
						icon: " ri-article-line",
						parentId: 15,
						link: "/logistique/charges",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.TRESORERIE]),
					},
					{
						id: 17,
						label: "MENUITEMS.LOGISTIQUE.LIST.ENGINS",
						icon: " ri-article-line",
						parentId: 15,
						link: "/logistique/engins",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.TRESORERIE]),
					},
					{
						id: 18,
						label: "MENUITEMS.LOGISTIQUE.LIST.CHAUFFEURS",
						icon: " ri-article-line",
						parentId: 15,
						link: "/logistique/chauffeurs",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA, PermissionEnum.TRESORERIE]),
					},
				],
			},

			{
				id: 19,
				label: "MENUITEMS.MODULES.LIST.ACHATS",
				icon: "ri-bank-card-line",
				isActive: this.authenticationService.hasPermission([
					PermissionEnum.ADMIN,
					PermissionEnum.CONTROLE_GESTION,
					PermissionEnum.ACHETEUR,
					PermissionEnum.SUPERVISEUR_ACHAT,
					PermissionEnum.GESTIONNAIREBC, PermissionEnum.TRESORERIE,
				]),
				subItems: [
					{
						id: 20,
						label: "MENUITEMS.MODULES.LIST.DEMANDESACHATLIST",
						parentId: 19,
						link: "/achat/demandes",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.ACHETEUR, PermissionEnum.SUPERVISEUR_ACHAT, PermissionEnum.TRESORERIE]),
					},
					{
						id: 20,
						label: "MENUITEMS.MODULES.LIST.PREPARATION",
						parentId: 19,
						link: "/achat/preparation",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.ACHETEUR, PermissionEnum.SUPERVISEUR_ACHAT, PermissionEnum.TRESORERIE]),
					},

					{
						id: 21,
						label: "MENUITEMS.MODULES.LIST.CONSULTATIONLIST",
						parentId: 19,
						link: "/achat/consultation-list",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.ACHETEUR, PermissionEnum.SUPERVISEUR_ACHAT, PermissionEnum.TRESORERIE]),
					},
					{
						id: 22,
						label: "MENUITEMS.MODULES.LIST.BONCOMMANDE",
						icon: " ri-article-line",
						parentId: 19,
						link: "/achat/bons-commandes",
						isActive: this.authenticationService.hasPermission([
							PermissionEnum.ADMIN,
							PermissionEnum.CONTROLE_GESTION,
							PermissionEnum.ACHETEUR,
							PermissionEnum.SUPERVISEUR_ACHAT,
							PermissionEnum.GESTIONNAIREBC, PermissionEnum.TRESORERIE,
						]),
					},
					{
						id: 23,
						label: "MENUITEMS.MODULES.LIST.RECEPTION",
						parentId: 19,
						link: "/achat/bons-livraison",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.ACHETEUR, PermissionEnum.SUPERVISEUR_ACHAT, PermissionEnum.GESTIONNAIREBC, PermissionEnum.TRESORERIE]),
					},
					{
						id: 24,
						label: "MENUITEMS.MODULES.LIST.RECEPTIONCHANTIER",
						parentId: 19,
						link: "/achat/rest-a-livre",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ACHETEUR, PermissionEnum.CONTROLE_GESTION, PermissionEnum.ADMIN, PermissionEnum.SUPERVISEUR_ACHAT, PermissionEnum.TRESORERIE]),
					},
				],
			},
			// IMPORTATIONS
			{
				id: 70,
				label: "Importations",
				icon: "ri-ship-line",
				isActive: this.authenticationService.hasPermission([
					PermissionEnum.ADMIN,
					PermissionEnum.CONTROLE_GESTION,
					PermissionEnum.CONTROLE_GESTION,
					PermissionEnum.ACHETEUR,
					PermissionEnum.SUPERVISEUR_ACHAT, PermissionEnum.TRESORERIE,
				]),
				subItems: [
					{
						id: 71,
						parentId: 70,
						label: "Dossiers d'importation",
						link: "/importation/dossiers",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.ACHETEUR, PermissionEnum.SUPERVISEUR_ACHAT, PermissionEnum.TRESORERIE]),
					},
					{
						id: 72,
						parentId: 70,
						label: "Suivi & frais d'approche",
						link: "/importation/recap",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.ACHETEUR, PermissionEnum.SUPERVISEUR_ACHAT, PermissionEnum.TRESORERIE]),
					},
				],
			},
			// GESTION DE COMMERCIAL
			{
				id: 25,
				label: "MENUITEMS.GESTION_COMMERCIAL.TEXT",
				icon: "ri-currency-line",
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.GESTIONNAIRE_COMMERCIAL, PermissionEnum.FINANCE, PermissionEnum.TRESORERIE, PermissionEnum.RESPONSABLE_PROJET, PermissionEnum.SUPERVISEUR]),
				subItems: [
					{
						id: 26,
						parentId: 25,
						label: "MENUITEMS.GESTION_COMMERCIAL.LIST.FACTURATION",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.GESTIONNAIRE_COMMERCIAL, PermissionEnum.FINANCE, PermissionEnum.TRESORERIE, PermissionEnum.RESPONSABLE_PROJET, PermissionEnum.SUPERVISEUR]),
						subItems: [
							{
								id: 27,
								parentId: 26,
								label: "MENUITEMS.GESTION_COMMERCIAL.LIST.DEVIS",
								link: "/gestion-commercial/devis",
								isActive: this.authenticationService.hasPermission([
									PermissionEnum.ADMIN,
									PermissionEnum.CONTROLE_GESTION,
									PermissionEnum.GESTIONNAIRE_COMMERCIAL,
									PermissionEnum.FINANCE, PermissionEnum.TRESORERIE,
									PermissionEnum.RESPONSABLE_PROJET,
									PermissionEnum.SUPERVISEUR,
								]),
							},
							{
								id: 28,
								parentId: 26,
								label: "MENUITEMS.GESTION_COMMERCIAL.LIST.ATTACHEMENTS",
								link: "/gestion-commercial/attachments",
								isActive: this.authenticationService.hasPermission([
									PermissionEnum.ADMIN,
									PermissionEnum.CONTROLE_GESTION,
									PermissionEnum.GESTIONNAIRE_COMMERCIAL,
									PermissionEnum.FINANCE, PermissionEnum.TRESORERIE,
								]),
							},
							{
								id: 29,
								parentId: 26,
								label: "MENUITEMS.GESTION_COMMERCIAL.LIST.DECOMPTE",
								link: "/gestion-commercial/decomptes",
								isActive: this.authenticationService.hasPermission([
									PermissionEnum.ADMIN,
									PermissionEnum.CONTROLE_GESTION,
									PermissionEnum.GESTIONNAIRE_COMMERCIAL,
									PermissionEnum.FINANCE, PermissionEnum.TRESORERIE,
									PermissionEnum.RESPONSABLE_PROJET,
								]),
							},

							{
								id: 30,
								parentId: 26,
								label: "MENUITEMS.GESTION_COMMERCIAL.LIST.FACTURE",
								link: "/gestion-commercial/factures",
								isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.GESTIONNAIRE_COMMERCIAL, PermissionEnum.FINANCE]),
							},
							{
								id: 31,
								parentId: 26,
								label: "MENUITEMS.GESTION_COMMERCIAL.LIST.PAIEMENTS",
								icon: "ri-secure-payment-line",
								link: "/gestion-commercial/paiements",
								isActive: this.authenticationService.hasPermission([
									PermissionEnum.ADMIN,
									PermissionEnum.CONTROLE_GESTION,
									PermissionEnum.GESTIONNAIRE_COMMERCIAL,
									PermissionEnum.FINANCE, PermissionEnum.TRESORERIE,
								]),
							},
						],
					},

					{
						id: 32,
						parentId: 25,
						label: "MENUITEMS.GESTION_COMMERCIAL.LIST.SUIVIE",
						icon: "ri-secure-payment-line",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.GESTIONNAIRE_COMMERCIAL]),
						subItems: [
							{
								id: 33,
								parentId: 32,
								label: "MENUITEMS.GESTION_COMMERCIAL.LIST.SUIVIE_CAUTION",
								link: "/gestion-commercial/cautions",
								isActive: this.authenticationService.hasPermission([
									PermissionEnum.ADMIN,
									PermissionEnum.CONTROLE_GESTION,
									PermissionEnum.GESTIONNAIRE_COMMERCIAL,
									PermissionEnum.FINANCE, PermissionEnum.TRESORERIE,
								]),
							},
							{
								id: 34,
								parentId: 32,
								label: "MENUITEMS.GESTION_COMMERCIAL.LIST.SUIVIE_RECOUVREMENT",
								link: "/gestion-commercial/suive-recouvrement",
								isActive: this.authenticationService.hasPermission([
									PermissionEnum.ADMIN,
									PermissionEnum.CONTROLE_GESTION,
									PermissionEnum.GESTIONNAIRE_COMMERCIAL,
									PermissionEnum.FINANCE, PermissionEnum.TRESORERIE,
								]),
							},
							{
								id: 35,
								parentId: 32,
								label: "MENUITEMS.GESTION_COMMERCIAL.LIST.SUIVIE_PROJET",
								link: "/gestion-commercial/suiveuse-projet",
								isActive: this.authenticationService.hasPermission([
									PermissionEnum.ADMIN,
									PermissionEnum.CONTROLE_GESTION,
									PermissionEnum.GESTIONNAIRE_COMMERCIAL,
									PermissionEnum.FINANCE, PermissionEnum.TRESORERIE,
								]),
							},

							{
								id: 36,
								parentId: 32,
								label: "MENUITEMS.GESTION_COMMERCIAL.LIST.SUIVIE_MARCHIE",
								link: "/gestion-commercial/suiveuse-global",
								isActive: this.authenticationService.hasPermission([
									PermissionEnum.ADMIN,
									PermissionEnum.CONTROLE_GESTION,
									PermissionEnum.GESTIONNAIRE_COMMERCIAL,
									PermissionEnum.FINANCE, PermissionEnum.TRESORERIE,
								]),
							},
						],
					},
					{
						id: 372,
						parentId: 25,
						label: "MENUITEMS.GESTION_COMMERCIAL.LIST.PREVISIONS",
						icon: "ri-line-chart-line",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.GESTIONNAIRE_COMMERCIAL, PermissionEnum.FINANCE, PermissionEnum.RESPONSABLE_PROJET]),
						subItems: [
							{
								id: 370,
								parentId: 372,
								label: "MENUITEMS.GESTION_COMMERCIAL.LIST.PREVISIONS_VENTES",
								link: "/gestion-commercial/previsions-ventes",
								isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.GESTIONNAIRE_COMMERCIAL, PermissionEnum.FINANCE, PermissionEnum.RESPONSABLE_PROJET]),
							},
							{
								id: 371,
								parentId: 372,
								label: "MENUITEMS.GESTION_COMMERCIAL.LIST.MES_PREVISIONS",
								link: "/gestion-commercial/previsions-ventes/saisie",
								isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.GESTIONNAIRE_COMMERCIAL, PermissionEnum.FINANCE, PermissionEnum.RESPONSABLE_PROJET]),
							},
							{
								id: 373,
								parentId: 372,
								label: "MENUITEMS.GESTION_COMMERCIAL.LIST.PREVISIONS_DASHBOARD",
								link: "/gestion-commercial/previsions-ventes/dashboard",
								isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.GESTIONNAIRE_COMMERCIAL, PermissionEnum.FINANCE, PermissionEnum.RESPONSABLE_PROJET]),
							},
						],
					},
					{
						id: 37,
						parentId: 25,
						label: "MENUITEMS.ADMIN.LIST.DASHBOARD",
						icon: "ri-bar-chart-line",
						link: "/gestion-commercial/projets",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.GESTIONNAIRE_COMMERCIAL, PermissionEnum.FINANCE, PermissionEnum.TRESORERIE]),
					},
				],
			},

			{
				id: 38,
				label: "MENUITEMS.TIMESHEET.TEXT",
				icon: "ri-map-pin-time-line",
				link: "/timesheet/",
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.BET, PermissionEnum.SUPERVISEUR_BET, PermissionEnum.SUPERVISEUR, PermissionEnum.FINANCE]),
				subItems: [
					{
						id: 39,
						label: "MENUITEMS.TIMESHEET.LIST.TACHES",
						icon: "ri-community-fill",
						link: "/timesheet/taches",
						parentId: 38,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.BET, PermissionEnum.SUPERVISEUR_BET, PermissionEnum.SUPERVISEUR]),
					},
					{
						id: 40,
						label: "MENUITEMS.TIMESHEET.LIST.CATEGORIE_TACHES",
						icon: "ri-community-fill",
						link: "/timesheet/categorie-taches",
						parentId: 38,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.SUPERVISEUR_BET, PermissionEnum.SUPERVISEUR]),
					},
					{
						id: 41,
						label: "MENUITEMS.TIMESHEET.LIST.PROJETS",
						icon: "ri-community-fill",
						link: "/timesheet/projets",
						parentId: 38,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.SUPERVISEUR_BET, PermissionEnum.SUPERVISEUR]),
					},
					{
						id: 42,
						label: "MENUITEMS.TIMESHEET.LIST.DASHBOARD",
						icon: "ri-community-fill",
						link: "/timesheet/dashboard",
						parentId: 38,
						isActive: this.authenticationService.hasPermission([
							PermissionEnum.ADMIN,
							PermissionEnum.BET,
							PermissionEnum.SUPERVISEUR_BET,
							PermissionEnum.SUPERVISEUR,
							PermissionEnum.FINANCE,
						]),
					},
				],
			},

			{
				id: 43,
				label: "MENUITEMS.RH.TEXT",
				icon: "ri-group-2-line",
				isActive: this.authenticationService.hasPermission([
					PermissionEnum.ADMIN,
					PermissionEnum.RH,
					PermissionEnum.ACHETEUR,
					PermissionEnum.SUPERVISEUR_ACHAT,
					PermissionEnum.SUPERVISEUR_BET,
					PermissionEnum.GESTIONNAIREBC,
					PermissionEnum.GESTIONNAIRE_COMMERCIAL,
					PermissionEnum.BET,
					PermissionEnum.SUPERVISEUR,
					PermissionEnum.DEMANDEUR,
					PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA,
					PermissionEnum.COLLABORATEUR,
					PermissionEnum.TRESORERIE_BASIC,
					PermissionEnum.TRESORERIE,
					PermissionEnum.CONTROLE_GESTION,
					PermissionEnum.MAGASINIER_INFORMATIQUE,
					PermissionEnum.FINANCE,
					PermissionEnum.COORDINATION_RH,
				]),
				subItems: [
					{
						id: 44,
						label: "MENUITEMS.RH.LIST.PERSONNEL",
						icon: "ri-community-fill",
						parentId: 43,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.RH]),
						subItems: [
							{
								id: 441,
								label: "MENUITEMS.RH.LIST.MIF_MAROC",
								link: "/rh/employes/mif-maroc",
								parentId: 44,
								isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.RH]),
							},
							{
								id: 442,
								label: "MENUITEMS.RH.LIST.HUMICLIMA",
								link: "/rh/employes/humiclima",
								parentId: 44,
								isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.RH]),
							},
							{
								id: 54,
								label: "MENUITEMS.RH.LIST.INTERMAIRE",
								link: "/rh/interimaires",
								parentId: 44,
								isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.RH]),
							},
						],
					},
					{
						id: 45,
						label: "MENUITEMS.RH.LIST.DEMANDES",
						icon: "ri-community-fill",
						link: "/rh/demandes",
						parentId: 43,
						isActive: this.authenticationService.hasPermission([
							PermissionEnum.ADMIN,
							PermissionEnum.CONTROLE_GESTION,
							PermissionEnum.RH,
							PermissionEnum.ACHETEUR,
							PermissionEnum.SUPERVISEUR_ACHAT,
							PermissionEnum.SUPERVISEUR_BET,
							PermissionEnum.GESTIONNAIREBC,
							PermissionEnum.GESTIONNAIRE_COMMERCIAL,
							PermissionEnum.BET,
							PermissionEnum.SUPERVISEUR,
							PermissionEnum.DEMANDEUR,
							PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA,
							PermissionEnum.COLLABORATEUR,
							PermissionEnum.TRESORERIE_BASIC,
							PermissionEnum.TRESORERIE,
							PermissionEnum.MAGASINIER_INFORMATIQUE,
							PermissionEnum.FINANCE,
							PermissionEnum.COORDINATION_RH,
						]),
						subItems: [
							{
								id: 451,
								label: "MENUITEMS.RH.LIST.DEMANDES_CONGE",
								link: "/rh/demandes/conge",
								parentId: 45,
								isActive: true,
							},
							{
								id: 452,
								label: "MENUITEMS.RH.LIST.DEMANDES_ATTESTATION_SALAIRE",
								link: "/rh/demandes/attestation-salaire",
								parentId: 45,
								isActive: true,
							},
							{
								id: 453,
								label: "MENUITEMS.RH.LIST.DEMANDES_ATTESTATION_TRAVAIL",
								link: "/rh/demandes/attestation-travail",
								parentId: 45,
								isActive: true,
							},
							{
								id: 454,
								label: "MENUITEMS.RH.LIST.DEMANDES_BULLETIN_PAIE",
								link: "/rh/demandes/bulletin-paie",
								parentId: 45,
								isActive: true,
							},
						],
					},
					{
						id: 46,
						label: "MENUITEMS.RH.LIST.NOTES_FRAIS",
						icon: "ri-community-fill",
						link: "/rh/note-frais",
						parentId: 43,
						isActive: this.authenticationService.hasPermission([
							PermissionEnum.ADMIN,
							PermissionEnum.CONTROLE_GESTION,
							PermissionEnum.RH,
							PermissionEnum.ACHETEUR,
							PermissionEnum.SUPERVISEUR_ACHAT,
							PermissionEnum.SUPERVISEUR_BET,
							PermissionEnum.GESTIONNAIREBC,
							PermissionEnum.GESTIONNAIRE_COMMERCIAL,
							PermissionEnum.BET,
							PermissionEnum.SUPERVISEUR,
							PermissionEnum.DEMANDEUR,
							PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA,
							PermissionEnum.COLLABORATEUR,
							PermissionEnum.TRESORERIE_BASIC,
							PermissionEnum.TRESORERIE,
							PermissionEnum.MAGASINIER_INFORMATIQUE,
							PermissionEnum.FINANCE,
							PermissionEnum.COORDINATION_RH,
						]),
					},
					{
						id: 46,
						label: "MENUITEMS.RH.LIST.TYPE_PROFESSIONNELE",
						icon: "ri-community-fill",
						link: "/rh/type-note-professionnelle",
						parentId: 43,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.RH]),
					},
					{
						id: 53,
						label: "MENUITEMS.RH.LIST.POINTAGE",
						icon: "ri-calendar-check-line",
						link: "/rh/pointage",
						parentId: 43,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.RH]),
					},
					{
						id: 55,
						label: "MENUITEMS.RH.LIST.AGENCE_INTERIM",
						icon: "ri-calendar-check-line",
						link: "/rh/agence-interim",
						parentId: 43,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.RH]),
					},
					{
						id: 561,
						label: "Suivi des loyers",
						icon: "ri-home-4-line",
						parentId: 43,
						isActive: this.authenticationService.hasPermission([
							PermissionEnum.ADMIN,
							PermissionEnum.SUPERVISEUR,
							PermissionEnum.COORDINATION_RH,
							PermissionEnum.TRESORERIE,
							PermissionEnum.CONTROLE_GESTION,
						]),
						subItems: [
							{
								id: 5610,
								label: "Bailleurs",
								link: "/rh/loyers/bailleurs",
								parentId: 561,
								isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.SUPERVISEUR, PermissionEnum.COORDINATION_RH]),
							},
							{
								id: 5611,
								label: "Baux",
								link: "/rh/loyers/baux",
								parentId: 561,
								isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.SUPERVISEUR, PermissionEnum.COORDINATION_RH]),
							},
							{
								id: 5612,
								label: "Charges Loyers",
								link: "/rh/loyers/echeances",
								parentId: 561,
								isActive: this.authenticationService.hasPermission([
									PermissionEnum.ADMIN,
									PermissionEnum.SUPERVISEUR,
									PermissionEnum.COORDINATION_RH,
									PermissionEnum.TRESORERIE,
									PermissionEnum.CONTROLE_GESTION,
								]),
							},
							{
								id: 5614,
								label: "Charges Eau/Élec",
								link: "/rh/loyers/charges",
								parentId: 561,
								isActive: this.authenticationService.hasPermission([
									PermissionEnum.ADMIN,
									PermissionEnum.SUPERVISEUR,
									PermissionEnum.COORDINATION_RH,
									PermissionEnum.TRESORERIE,
									PermissionEnum.CONTROLE_GESTION,
								]),
							},

							{
								id: 5613,
								label: "Récapitulatif",
								link: "/rh/loyers/recap",
								parentId: 561,
								isActive: this.authenticationService.hasPermission([
									PermissionEnum.ADMIN,
									PermissionEnum.SUPERVISEUR,
									PermissionEnum.COORDINATION_RH,
									PermissionEnum.TRESORERIE,
									PermissionEnum.CONTROLE_GESTION,
								]),
							},
						],
					},
				],
			},
			{
				id: 56,
				label: "MENUITEMS.FINANCE.TEXT",
				icon: "ri-secure-payment-line",
				isActive: this.authenticationService.hasPermission([
					PermissionEnum.ADMIN,
					PermissionEnum.TRESORERIE,
					PermissionEnum.SUPERVISEUR,
					PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA,
					PermissionEnum.ACHETEUR,
					PermissionEnum.SUPERVISEUR_ACHAT,
					PermissionEnum.BET,
					PermissionEnum.GESTIONNAIRE_COMMERCIAL,
					PermissionEnum.SUPERVISEUR_BET,
					PermissionEnum.CHIFFRAGE,
					PermissionEnum.COLLABORATEUR,
					PermissionEnum.TRESORERIE_BASIC,
					PermissionEnum.DEMANDEUR,
					PermissionEnum.RH,
					PermissionEnum.CONTROLE_GESTION,
					PermissionEnum.MAGASINIER_INFORMATIQUE,
				]),
				subItems: [
					{
						id: 57,
						label: "MENUITEMS.FINANCE.LIST.DEMANDE_PAIEMENT",
						icon: "ri-community-fill",
						link: "/finance/demandes-paiement",
						parentId: 56,
						isActive: this.authenticationService.hasPermission([
							PermissionEnum.ADMIN,
							PermissionEnum.CONTROLE_GESTION,
							PermissionEnum.TRESORERIE,
							PermissionEnum.SUPERVISEUR,
							PermissionEnum.MAGASINIER, PermissionEnum.MASTER_DATA,
							PermissionEnum.ACHETEUR,
							PermissionEnum.SUPERVISEUR_ACHAT,
							PermissionEnum.BET,
							PermissionEnum.GESTIONNAIRE_COMMERCIAL,
							PermissionEnum.SUPERVISEUR_BET,
							PermissionEnum.CHIFFRAGE,
							PermissionEnum.COLLABORATEUR,
							PermissionEnum.TRESORERIE_BASIC,
							PermissionEnum.DEMANDEUR,
							PermissionEnum.RH,
							PermissionEnum.MAGASINIER_INFORMATIQUE,
						]),
					},
					{
						id: 58,
						label: "MENUITEMS.FINANCE.LIST.CATEGORIES_PAIEMENT",
						icon: "ri-list-check-2",
						link: "/finance/categories-paiement",
						parentId: 56,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.TRESORERIE]),
					},
					{
						id: 59,
						label: "MENUITEMS.FINANCE.LIST.REGLEMENTS_FOURNISSEURS",
						icon: "ri-bank-line",
						link: "/finance/reglements-fournisseurs",
						parentId: 56,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.TRESORERIE]),
					},
					{
						id: 60,
						label: "Taux",
						icon: "ri-percent-line",
						link: "/finance/taux",
						parentId: 56,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.TRESORERIE]),
					},
				],
			},
			// BUDGET =============================================
			{
				id: 90,
				label: "MENUITEMS.BUDGET.TEXT",
				icon: "ri-wallet-3-line",
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.FINANCE, PermissionEnum.TRESORERIE]),
				subItems: [
					{
						id: 93,
						parentId: 90,
						label: "MENUITEMS.BUDGET.LIST.ANALYSE",
						link: "/budget/analyse",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.FINANCE, PermissionEnum.TRESORERIE]),
					},
					{
						id: 91,
						parentId: 90,
						label: "MENUITEMS.BUDGET.LIST.CHARGES_FIXES",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.FINANCE, PermissionEnum.TRESORERIE]),
						subItems: [
							{ id: 911, parentId: 91, label: "MENUITEMS.BUDGET.LIST.VEHICULES", link: "/budget/vehicule" },
							{ id: 912, parentId: 91, label: "MENUITEMS.BUDGET.LIST.MUTUELLE", link: "/budget/mutuelle" },
							{ id: 913, parentId: 91, label: "MENUITEMS.BUDGET.LIST.ABONNEMENTS_TELEPHONIQUES", link: "/budget/abonnement-telephonique" },
							{ id: 914, parentId: 91, label: "MENUITEMS.BUDGET.LIST.ASSURANCES", link: "/budget/assurance" },
						],
					},
					{
						id: 92,
						parentId: 90,
						label: "MENUITEMS.BUDGET.LIST.CHARGES_VARIABLES",
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.FINANCE, PermissionEnum.TRESORERIE]),
						subItems: [
							{ id: 921, parentId: 92, label: "MENUITEMS.BUDGET.LIST.TELEPHONES", link: "/budget/telephone" },
							{ id: 922, parentId: 92, label: "MENUITEMS.BUDGET.LIST.ELECTRICITE", link: "/budget/electricite" },
							{ id: 923, parentId: 92, label: "MENUITEMS.BUDGET.LIST.CARBURANT", link: "/budget/carburant" },
							{ id: 924, parentId: 92, label: "MENUITEMS.BUDGET.LIST.HONORAIRES", link: "/budget/honoraire" },
						],
					},
				],
			},
			// CONFIGURATION =========================================

			{
				id: 46,
				label: "MENUITEMS.CONFIGURATION.TEXT",
				isTitle: true,
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.GESTIONNAIRE_COMMERCIAL, PermissionEnum.TRESORERIE, PermissionEnum.MASTER_DATA]),
			},

			{
				id: 47,
				label: "MENUITEMS.CONFIGURATION.LIST.UTILISATEURS",
				icon: "ri-user-settings-line",
				link: "/administration/liste/administratif",
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.RH]),
			},

			{
				id: 4701,
				label: "Rôles & permissions",
				icon: "ri-shield-keyhole-line",
				link: "/administration/roles",
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN]),
			},

			{
				id: 4703,
				label: "Traçabilité",
				icon: "ri-history-line",
				link: "/administration/audit-logs",
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN]),
			},

			{
				id: 4704,
				label: "Installations de l'app",
				icon: "ri-smartphone-line",
				link: "/administration/pwa-installations",
				isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN]),
			},

			{
				id: 4705,
				label: "Migration des composants",
				icon: "ri-file-transfer-line",
				link: "/administration/migration-composants",
				// Désactivé après la migration : remettre hasPermission([PermissionEnum.ADMIN]) pour réafficher l'entrée
				isActive: false,
			},

			{
				id: 48,
				label: "MENUITEMS.CONFIGURATION.LIST.BASE",
				icon: " ri-database-2-line",
				link: "/administration/settings",
				isActive: this.authenticationService.hasPermission([
					PermissionEnum.ADMIN,
					PermissionEnum.CONTROLE_GESTION,
					PermissionEnum.GESTIONNAIRE_COMMERCIAL,
					PermissionEnum.DEMANDEUR,
					PermissionEnum.CHIFFRAGE,
					PermissionEnum.TRESORERIE,
					PermissionEnum.MASTER_DATA,
				]),
				subItems: [
					{
						id: 49,
						label: "MENUITEMS.CONFIGURATION.LIST.CLIENTS",
						icon: "ri-community-fill",
						link: "/administration/clients",
						parentId: 48,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.GESTIONNAIRE_COMMERCIAL]),
					},
					{
						id: 50,
						label: "MENUITEMS.CONFIGURATION.LIST.FOURNISSEURS",
						icon: "ri-community-fill",
						link: "/gestion/fournisseurs",
						parentId: 48,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.TRESORERIE]),
					},
					{
						id: 5101,
						label: "MENUITEMS.CONFIGURATION.LIST.PROJECTS_NEGOCIATION",
						icon: "ri-community-fill",
						link: "/administration/projets-negociation",
						parentId: 48,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.GESTIONNAIRE_COMMERCIAL, PermissionEnum.MASTER_DATA]),
					},
					{
						id: 51,
						label: "MENUITEMS.CONFIGURATION.LIST.PROJECTS",
						icon: "ri-community-fill",
						link: "/administration/projets",
						parentId: 48,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.GESTIONNAIRE_COMMERCIAL, PermissionEnum.MASTER_DATA]),
					},

					{
						id: 52,
						label: "MENUITEMS.ADMIN.LIST.ARTICLE",
						icon: "mdi mdi-format-list-text",
						link: "/gestion/base-articles",
						parentId: 48,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.CONTROLE_GESTION, PermissionEnum.DEMANDEUR, PermissionEnum.CHIFFRAGE, PermissionEnum.MASTER_DATA]),
					},
					{
						id: 60,
						label: "MENUITEMS.CONFIGURATION.LIST.BANQUES_EMETTRICES",
						icon: "ri-bank-line",
						link: "/administration/banques-emettrices",
						parentId: 48,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN, PermissionEnum.TRESORERIE]),
					},
					/*{
						id: 53,
						label: "MENUITEMS.CONFIGURATION.LIST.SETTINGS",
						icon: "mdi mdi-format-list-text",
						link: "/administration/settings",
						parentId: 48,
						isActive: this.authenticationService.hasPermission([PermissionEnum.ADMIN]),
					},*/
				],
			},
		];
	}
}
